/**
 * Parlays View Utilities
 * Extracted helper functions for tracked parlay display and logic
 */

import type { ScoringParlay } from '@hooks/useStrategyStats';
import type { TrackedParlay, TrackedParlayLeg } from '@hooks/useTrackedParlays';
import { isTeamBet } from '@parlay-picker/shared/correlationUtils';
import { calculateDnpAdjustedOdds, countEffectiveDnpLegs } from '@parlay-picker/shared/dnpUtils';
import { formatStatLabel, legTextWithMarket } from '@parlay-picker/shared/statLabels';
import { formatLegMatchup } from '@parlay-picker/shared/teamParsing';
import { getTeamAbbr } from '@parlay-picker/shared/teams';
import type { LucideIcon } from 'lucide-react';
import { Gem, Pencil, Shield, Sparkles, Target, Ticket, TrendingUp } from 'lucide-react';
import { formatPropDisplay } from '../picks/utils';

// =============================================================================
// Types
// =============================================================================

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

// =============================================================================
// Tier & Result Styles
// =============================================================================

// Unified theme-conscious style maps (now universal across all themes)
/** Maps tier icon names to Lucide icon components */
export const TIER_ICONS: Record<string, LucideIcon> = {
  shield: Shield,
  gem: Gem,
  'trending-up': TrendingUp,
  target: Target,
  ticket: Ticket,
  pencil: Pencil,
  sparkles: Sparkles,
};

// Fixed brand colors for risk tiers — NOT theme-dependent.
// Using theme semantic colors (primary, warning) causes collisions
// in themes where primary === warning (e.g., Sunset Amber).
// These match mobile's RISK_TIER palette for cross-platform consistency.
export const TIER_STYLES: Record<string, string> = {
  'Safe Play': 'bg-teal-500/15 text-teal-400 border-teal-500/30',
  'Value Play': 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30',
  'Upside Play': 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  'Long Shot': 'bg-red-500/15 text-red-400 border-red-500/30',
  Custom: 'bg-theme-surface-hover text-theme-text-secondary border-theme-border',
  'AI Improved': 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
};

export const RESULT_STYLES: Record<string, string> = {
  won: 'bg-theme-success/20 text-theme-success',
  lost: 'bg-theme-danger/20 text-theme-danger',
  void: 'bg-purple-500/15 text-purple-400',
  pending: 'bg-theme-warning/20 text-theme-warning',
  push: 'bg-theme-surface-hover text-theme-text-secondary',
};

// =============================================================================
// Leg Helpers
// =============================================================================

/** Check if a tracked leg is a game-level prop (spread, total, moneyline) */
export function isTrackedLegGameLevel(leg: TrackedParlayLeg): boolean {
  return isTeamBet(leg.statType);
}

/** Check if a tracked leg is a total prop (Over/Under game total) */
export function isTrackedLegTotal(leg: TrackedParlayLeg): boolean {
  const statType = leg.statType?.toLowerCase() || '';
  return statType === 'total' || statType === 'totals';
}

/** Check if a leg is eligible for a progress bar (player props and totals only) */
export function isProgressBarEligible(leg: TrackedParlayLeg): boolean {
  if (leg.dnp) return false;
  if (leg.player && !isTrackedLegGameLevel(leg)) return true;
  if (isTrackedLegTotal(leg)) return true;
  return false;
}

/** Format matchup prefix for game-level props using abbreviated team names */
export function formatMatchupPrefix(leg: TrackedParlayLeg): string {
  const team = leg.team || '';
  if (!team) return '';

  const matchup = formatLegMatchup(leg, (name) => getTeamAbbr(name, leg.leagueKey));
  if (matchup) return `${matchup} - `;

  return `${getTeamAbbr(team, leg.leagueKey)} - `;
}

/** Format a tracked leg's prop for display */
export function formatTrackedLegProp(leg: TrackedParlayLeg): string {
  const prop = leg.prop || '';

  // If prop contains numbers (e.g. "Over 11.5 PRA"), format the whole string
  if (/\d/.test(prop)) {
    return legTextWithMarket(formatPropDisplay(prop) || prop, leg.marketKey);
  }

  // If prop is a bare stat key or label, build a display string from components
  const parts: string[] = [];
  if (leg.overUnder) parts.push(leg.overUnder);
  if (leg.line != null) parts.push(String(leg.line));
  // Use the prop string if available, otherwise fall back to statType
  const label = prop ? formatPropDisplay(prop) || prop : formatStatLabel(leg.statType);
  if (label) parts.push(label);

  return legTextWithMarket(parts.join(' ') || prop, leg.marketKey);
}

// =============================================================================
// Scoring Helpers
// =============================================================================

/** Convert a TrackedParlay to ScoringParlay format for scoring functions */
export function toScoringParlay(parlay: TrackedParlay): ScoringParlay {
  return {
    name: parlay.tier || 'Custom',
    confidence: parlay.confidence || undefined,
    legs: parlay.legs.map((leg) => ({
      player: leg.player || undefined,
      team: leg.team || undefined,
      statType: leg.statType || undefined,
      propType: leg.statType || undefined,
      line: leg.line != null ? Number(leg.line) : undefined,
      overUnder: leg.overUnder || undefined,
      direction: leg.overUnder || undefined,
      opponent: leg.opponentTeam || undefined,
      isB2b: leg.isB2b ?? undefined,
      blowoutRisk: leg.blowoutRisk || undefined,
      gameId: leg.gameId ?? undefined,
      leagueKey: leg.leagueKey,
    })),
  };
}

// =============================================================================
// Effective Odds Calculation
// =============================================================================

/** Calculate effective odds when legs are voided (DNP) */
export function calculateEffectiveOdds(
  parlay: TrackedParlay,
  isPlayerDNP: (
    gameId: string | null,
    playerId: number | null,
    playerName: string | null,
  ) => boolean | null,
): string | null {
  // Shared with mobile so the DNP-adjusted odds can't drift between platforms.
  return calculateDnpAdjustedOdds(parlay, isPlayerDNP);
}

/** Count DNP legs in a parlay */
export function countDnpLegs(
  parlay: TrackedParlay,
  isPlayerDNP: (
    gameId: string | null,
    playerId: number | null,
    playerName: string | null,
  ) => boolean | null,
): number {
  return countEffectiveDnpLegs(parlay.legs, isPlayerDNP);
}

/** Calculate current streak from ordered parlay results */
export function calculateStreak(parlays: TrackedParlay[]): {
  count: number;
  type: 'W' | 'L' | '-';
} {
  // Order by settlement time (most-recently-decided first) so the current streak
  // reflects settlement order, matching the server leaderboard, regardless of the
  // list's creation-order sort. Settled parlays always carry settledAt; when it is
  // absent (only in synthetic data) the stable sort keeps the input order.
  const settled = parlays
    .filter((p) => p.result === 'won' || p.result === 'lost')
    .sort((a, b) => (b.settledAt ?? '').localeCompare(a.settledAt ?? ''));
  if (settled.length === 0) return { count: 0, type: '-' };

  const firstResult = settled[0].result;
  let count = 0;
  for (const p of settled) {
    if (p.result === firstResult) {
      count++;
    } else {
      break;
    }
  }

  return { count, type: firstResult === 'won' ? 'W' : 'L' };
}
